import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { BlobPreloader } from '../components/BlobPreloader';
import { 
  ArrowLeft, 
  Play,
  Pause,
  Volume2,
  VolumeX,
  Trash2,
  ChevronRight,
  Clock,
  Mic,
  Calendar,
  Timer,
  Phone as PhoneIcon,
  Sparkles,
  Target,
  AlertCircle,
  ArrowRight,
  Tag,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sleek Audio Player with click-to-seek and ±15s skip buttons
const AudioPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const formatTime = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) audioRef.current.currentTime = pct * duration;
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const handlePlayPause = async () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.error('Audio play error:', err);
        }
        setIsLoading(false);
      }
    }
  };

  if (!audioUrl) return null;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-5 mb-12 ring-1 ring-slate-800/50 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-1.5 h-1.5 rounded-full bg-[#2eb4ac] animate-pulse" />
        <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#5fd6cf]">Recording</span>
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => setIsPlaying(false)}
      />
      
      <div className="flex items-center gap-4">
        {/* Skip Back -15s */}
        <button 
          onClick={() => skipTime(-15)}
          className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-full text-sm font-semibold hover:bg-slate-700 transition-colors"
          title="Skip back 15 seconds"
        >
          −15s
        </button>
        
        {/* Play/Pause Button */}
        <button 
          onClick={handlePlayPause}
          className="w-14 h-14 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
        >
          {isLoading ? (
            <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-6 h-6 text-slate-900" />
          ) : (
            <Play className="w-6 h-6 text-slate-900 ml-1" />
          )}
        </button>
        
        {/* Skip Forward +15s */}
        <button 
          onClick={() => skipTime(15)}
          className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-full text-sm font-semibold hover:bg-slate-700 transition-colors"
          title="Skip forward 15 seconds"
        >
          +15s
        </button>
        
        <div className="flex-1">
          <div className="h-2 bg-slate-700 rounded-full cursor-pointer mb-2 hover:h-2.5 transition-all" onClick={handleSeek}>
            <div 
              className="h-full bg-gradient-to-r from-[#2eb4ac] to-emerald-400 rounded-full pointer-events-none"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-400 font-mono tabular-nums">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        <button 
          onClick={() => {
            const speeds = [1, 1.5, 2, 0.75];
            const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
            setSpeed(next);
            if (audioRef.current) audioRef.current.playbackRate = next;
          }}
          className="px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700"
        >
          {speed}×
        </button>
      </div>
    </div>
  );
};

// ============ Summary Section (combines brief + detailed narrative) ============
const BriefSummarySection = ({ call, isInterested, analysis }) => {
  return (
    <div className="space-y-10" data-testid="summary-section">
      {/* Section title */}
      <div className="flex items-center gap-2.5">
        <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#2eb4ac] to-emerald-500" />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.18em]">Summary</p>
      </div>

      {/* Headline Card — now leads with the Call Category instead of a generic
          "Price advantage" type phrase. Falls back to a neutral label when the
          category is missing. */}
      <div className={`rounded-2xl p-6 border ${
        isInterested 
          ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-100' 
          : 'bg-gradient-to-br from-rose-50 to-white border-rose-100'
      }`}>
        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isInterested ? 'text-emerald-600' : 'text-rose-600'}`}>
          Call Category
        </p>
        <h2 className="text-xl font-bold text-slate-900 leading-tight mb-3" data-testid="summary-category-headline">
          {call.qc_category || 'Uncategorised'}
        </h2>
        {call.summary && (
          <p className="text-slate-600 leading-relaxed text-sm whitespace-pre-line">{call.summary}</p>
        )}
      </div>

      {/* Key Interest Signals or Contributing Reasons */}
      {isInterested && analysis?.drivers?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Key Interest Signals</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {analysis.drivers.map((d, i) => (
              <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-full text-sm font-medium border border-emerald-100">{d}</span>
            ))}
          </div>
        </div>
      )}

      {!isInterested && analysis?.secondary_reasons?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contributing Factors</p>
          </div>
          <ul className="space-y-2">
            {analysis.secondary_reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0 mt-2" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isInterested && analysis?.focus?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-3.5 h-3.5 text-[#2eb4ac]" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">What They're Looking For</p>
          </div>
          <ul className="space-y-2">
            {analysis.focus.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700 text-sm">
                <ChevronRight className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Points to address / missed opportunities */}
      {(analysis?.friction_points?.length > 0 || analysis?.missed_opportunities?.length > 0) && (
        <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-3.5 h-3.5 text-amber-700" />
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
              {isInterested ? 'Points to Address' : 'Missed Opportunities'}
            </p>
          </div>
          <ul className="space-y-1.5">
            {(analysis.friction_points || analysis.missed_opportunities || []).map((item, i) => (
              <li key={i} className="text-amber-900 text-sm flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 mt-2" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next action */}
      {call.next_action && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="w-3.5 h-3.5 text-[#2eb4ac]" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recommended Next Step</p>
          </div>
          <p className="text-slate-800 leading-relaxed text-sm">{call.next_action}</p>
        </div>
      )}

      {/* At-a-glance tags */}
      {(call.key_topics?.length > 0 || call.product_mentions?.length > 0 || call.brand_mentions?.length > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-3.5 h-3.5 text-slate-400" />
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">At a glance</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {call.product_mentions?.map((p, i) => (
              <span key={`p-${i}`} className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{p}</span>
            ))}
            {call.brand_mentions?.map((b, i) => (
              <span key={`b-${i}`} className="px-2.5 py-1 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium">{b}</span>
            ))}
            {call.key_topics?.slice(0, 8).map((t, i) => (
              <span key={`t-${i}`} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Tabular Breakdown Section ============
const TabularBreakdownSection = ({ call, isInterested, analysis }) => {
  // Group items by theme — each group renders as a horizontal pill row.
  // Themes follow Helix's domain language: signal, objection, mention, etc.
  const groups = [
    {
      title: 'Interest Signals',
      tone: 'positive',
      items: [
        ...(isInterested && analysis?.drivers ? analysis.drivers.map(v => ({ label: v, kind: 'Driver' })) : []),
        ...(isInterested && analysis?.focus ? analysis.focus.map(v => ({ label: v, kind: 'Focus' })) : []),
        ...((call.buying_signals || []).map(v => ({ label: v, kind: 'Signal' }))),
      ],
    },
    {
      title: 'Objections & Friction',
      tone: 'negative',
      items: [
        ...(!isInterested && analysis?.primary_reason ? [{ label: analysis.primary_reason, kind: 'Primary' }] : []),
        ...(!isInterested && analysis?.secondary_reasons ? analysis.secondary_reasons.map(v => ({ label: v, kind: 'Secondary' })) : []),
        ...((analysis?.friction_points || []).map(v => ({ label: v, kind: 'Friction' }))),
        ...((call.key_objections || []).map(v => ({ label: v, kind: 'Objection' }))),
      ],
    },
    {
      title: 'Products Mentioned',
      tone: 'product',
      items: (call.product_mentions || []).map(v => ({ label: v })),
    },
    {
      title: 'Brands Mentioned',
      tone: 'brand',
      items: (call.brand_mentions || []).map(v => ({ label: v })),
    },
    {
      title: 'Competitor Brands',
      tone: 'negative',
      items: (call.competitor_brand_mentions || []).map(v => ({ label: v })),
    },
    {
      title: 'Key Topics',
      tone: 'neutral',
      items: (call.key_topics || []).map(v => ({ label: v })),
    },
    {
      title: 'Stage & Recovery',
      tone: 'neutral',
      items: [
        ...(isInterested && analysis?.buying_stage ? [{ label: analysis.buying_stage, kind: 'Buying Stage' }] : []),
        ...(!isInterested && analysis?.recoverability ? [{ label: analysis.recoverability, kind: 'Recoverability' }] : []),
        ...((analysis?.missed_opportunities || []).map(v => ({ label: v, kind: 'Missed Opportunity' }))),
      ],
    },
  ].filter(g => (g.items || []).length > 0);

  // Dedupe items within each group (case-insensitive on label)
  groups.forEach(g => {
    const seen = new Set();
    g.items = g.items.filter(it => {
      const k = (it.label || '').toString().trim().toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  });

  const toneTheme = {
    positive: { bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-100', label: 'text-emerald-600', dot: 'bg-emerald-500' },
    negative: { bg: 'bg-rose-50',     text: 'text-rose-700',    ring: 'ring-rose-100',    label: 'text-rose-600',    dot: 'bg-rose-500' },
    product:  { bg: 'bg-purple-50',   text: 'text-purple-700',  ring: 'ring-purple-100',  label: 'text-purple-600',  dot: 'bg-purple-500' },
    brand:    { bg: 'bg-cyan-50',     text: 'text-cyan-700',    ring: 'ring-cyan-100',    label: 'text-cyan-600',    dot: 'bg-cyan-500' },
    neutral:  { bg: 'bg-slate-100',   text: 'text-slate-700',   ring: 'ring-slate-200',   label: 'text-slate-500',   dot: 'bg-slate-400' },
  };

  const totalCount = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div data-testid="tabular-breakdown-section">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-1 h-5 rounded-full bg-gradient-to-b from-purple-400 to-purple-600" />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.18em]">Tabular Breakdown</p>
      </div>
      <p className="text-sm text-slate-400 ml-3.5 mb-6">{totalCount} insights · {groups.length} categories</p>

      {totalCount === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          No structured insights available for this call yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groups.map(g => {
            const t = toneTheme[g.tone] || toneTheme.neutral;
            return (
              <div
                key={g.title}
                className="bg-white rounded-2xl border border-slate-200 p-5"
                data-testid={`breakdown-group-${g.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                    <h3 className={`text-xs font-semibold uppercase tracking-wider ${t.label}`}>
                      {g.title}
                    </h3>
                  </div>
                  <span className="text-xs text-slate-400 tabular-nums">{g.items.length}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((it, i) => (
                    <span
                      key={`${g.title}-${i}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ring-1 ${t.bg} ${t.text} ${t.ring}`}
                    >
                      {it.kind && (
                        <span className={`text-[10px] font-bold tracking-wider uppercase opacity-70 ${t.text}`}>
                          {it.kind}
                        </span>
                      )}
                      {it.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ============ Transcript Section ============
const TranscriptSection = ({ call }) => {
  const transcript = call.transcript_dialogue || call.transcript;
  if (!transcript) return null;
  const lines = transcript.split('\n').filter(l => l.trim());

  return (
    <section className="mb-12" data-testid="transcript-section">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="w-1 h-5 rounded-full bg-gradient-to-b from-slate-400 to-slate-600" />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-[0.18em]">Transcript</p>
        <span className="text-xs text-slate-400 ml-1 tabular-nums">· {lines.length} turns</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 max-h-[600px] overflow-y-auto">
        {lines.map((line, idx) => {
          const isAgent = line.toLowerCase().includes('sales') || line.toLowerCase().includes('agent') || idx % 2 === 0;
          const text = line.replace(/^\[?\d+:\d+\]?\s*(Sales\s*(?:Rep(?:resentative)?)?|Customer|Speaker\s*\d*|Agent|Client)[\s:]*/i, '').trim();
          return (
            <div key={idx} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
              <div className="max-w-[85%]">
                <p className={`text-xs font-medium mb-1 ${isAgent ? 'text-[#2eb4ac]' : 'text-slate-400'}`}>
                  {isAgent ? 'Sales Rep' : 'Customer'}
                </p>
                <div className={`px-4 py-3 rounded-2xl text-sm ${
                  isAgent ? 'bg-slate-100 text-slate-800 rounded-tl-sm' : 'bg-[#2eb4ac]/10 text-slate-800 rounded-tr-sm'
                }`}>
                  {text || line}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ============ Related Calls Section (other calls from same seller) ============
const RelatedCallsSection = ({ currentCallId, filename }) => {
  const [relatedCalls, setRelatedCalls] = useState([]);
  const [phone, setPhone] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(true);
  const [expandedCallIds, setExpandedCallIds] = useState(new Set());

  useEffect(() => {
    const extractPhone = (fname) => {
      if (!fname) return null;
      // Mirror backend logic: strip extension, take part before first dash, drop +country, last 10 digits
      let name = fname.replace(/\.(mp3|wav|m4a|webm|ogg)$/i, '');
      if (name.includes('_')) name = name.split('_').slice(1).join('_');
      const first = name.split('-')[0].replace('+', '').replace(/\s+/g, '').trim();
      const digits = first.replace(/\D/g, '');
      if (digits.length >= 10) return digits.slice(-10);
      // Fallback: any 10-digit run anywhere
      const m = fname.match(/(\d{10})/);
      return m ? m[1] : null;
    };

    const fetchRelated = async () => {
      const p = extractPhone(filename);
      setPhone(p);
      if (!p) { setLoadingRelated(false); return; }
      try {
        const res = await axios.get(`${API}/calls/by-phone/${p}?sort_order=desc`);
        const others = (res.data.calls || []).filter(c => c.id !== currentCallId);
        setRelatedCalls(others);
      } catch (err) {
        console.error('Failed to load related calls', err);
      } finally {
        setLoadingRelated(false);
      }
    };
    fetchRelated();
  }, [currentCallId, filename]);

  const toggleExpand = (id) => {
    setExpandedCallIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loadingRelated) {
    return (
      <section className="mb-12" data-testid="related-calls-section-loading">
        <div className="h-6 w-40 bg-slate-100 rounded animate-pulse" />
      </section>
    );
  }

  if (!phone || relatedCalls.length === 0) return null;

  return (
    <section className="mb-12" data-testid="related-calls-section">
      <div className="flex items-baseline gap-3 mb-6">
        <h2 className="text-xl font-bold text-slate-900">Related Calls</h2>
        <span className="text-sm text-slate-400">{relatedCalls.length} other call{relatedCalls.length !== 1 ? 's' : ''} from {phone}</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
        {relatedCalls.map(rc => {
          const isExpanded = expandedCallIds.has(rc.id);
          const rcInterested = rc.final_verdict === 'Interested';
          const rcAnalysis = rcInterested ? rc.interested_analysis : rc.not_interested_analysis;
          const headline = rcInterested 
            ? (rcAnalysis?.drivers?.[0] || 'Interested') 
            : (rcAnalysis?.primary_reason || 'Not interested');

          return (
            <div key={rc.id}>
              {/* Compact row */}
              <div 
                className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4"
                onClick={() => toggleExpand(rc.id)}
                data-testid={`related-call-${rc.id}`}
              >
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">
                      {rc.analyzed_at ? new Date(rc.analyzed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </span>
                    {rc.final_verdict === 'Interested' ? (
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">Interested</span>
                    ) : rc.final_verdict === 'Not Interested' ? (
                      <span className="px-2 py-0.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-full">Not Interested</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">Not Analysed</span>
                    )}
                    {rc.lifecycle_stage && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        rc.lifecycle_stage === 'Hot' ? 'bg-orange-50 text-orange-700' :
                        rc.lifecycle_stage === 'Warm' ? 'bg-yellow-50 text-yellow-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        {rc.lifecycle_stage}
                      </span>
                    )}
                    <span className="text-sm text-slate-500 truncate">{headline}</span>
                  </div>
                </div>
                <Link 
                  to={`/calls/${rc.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-medium text-[#2eb4ac] hover:underline whitespace-nowrap"
                  data-testid={`view-related-${rc.id}`}
                >
                  Open →
                </Link>
              </div>

              {/* Expanded drilldown */}
              {isExpanded && (
                <div className="bg-slate-50 px-6 py-5 border-t border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Brief summary side */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Summary</p>
                      <p className="text-sm text-slate-700 leading-relaxed mb-4">{rc.summary || 'No summary available'}</p>
                      {rcInterested && rcAnalysis?.drivers?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {rcAnalysis.drivers.slice(0, 4).map((d, i) => (
                            <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">{d}</span>
                          ))}
                        </div>
                      )}
                      {!rcInterested && rcAnalysis?.secondary_reasons?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {rcAnalysis.secondary_reasons.slice(0, 4).map((r, i) => (
                            <span key={i} className="px-2 py-0.5 bg-rose-50 text-rose-700 text-xs rounded-full">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Key topics side */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">At a glance</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(rc.product_mentions || []).slice(0, 6).map((p, i) => (
                          <span key={`p${i}`} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">{p}</span>
                        ))}
                        {(rc.brand_mentions || []).slice(0, 4).map((b, i) => (
                          <span key={`b${i}`} className="px-2 py-0.5 bg-cyan-50 text-cyan-700 text-xs rounded-full">{b}</span>
                        ))}
                        {(rc.key_topics || []).slice(0, 6).map((t, i) => (
                          <span key={`t${i}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};


const CallDetail = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get the previous path from location state, or default to call-logs
  const previousPath = location.state?.from || '/call-logs';
  const previousFilters = location.state?.filters || '';

  const handleGoBack = () => {
    // Use browser history if available, otherwise navigate to previous path with filters
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(previousPath + previousFilters);
    }
  };

  useEffect(() => {
    const fetchCall = async () => {
      try {
        const res = await axios.get(`${API}/calls/${callId}`);
        setCall(res.data);
      } catch (error) {
        toast.error('Failed to load call');
      } finally {
        setLoading(false);
      }
    };
    fetchCall();
  }, [callId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <BlobPreloader size={80} label="Loading call..." />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400 mb-4">Call not found</p>
        <button onClick={handleGoBack} className="text-[#2eb4ac]">← Back to calls</button>
      </div>
    );
  }

  const isInterested = call.final_verdict === 'Interested';
  const analysis = isInterested ? call.interested_analysis : call.not_interested_analysis;

  // Parse a clean customer name + phone from the filename for the hero,
  // falling back to detected/registered names from the call payload.
  const parseFilenameMeta = (filename) => {
    if (!filename) return { name: '', phone: '' };
    let base = filename.replace(/\.[^/.]+$/, '');
    // Strip leading UUID prefix (uuid_<rest>)
    base = base.replace(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/, '');
    // Pattern 1: Name(phone)_date
    const m1 = base.match(/^(.+?)\((\+?\d{6,15})\)/);
    if (m1) {
      const phone = m1[2].replace(/^\+?(91)?/, '').slice(-10);
      return { name: m1[1].trim(), phone };
    }
    // Pattern 2: Name-date  (e.g. 'Kaleem Inamdar-2603241228')
    const m2 = base.match(/^([^-_]+(?:\s+[^-_]+)*?)[-_](\d{6,})/);
    if (m2 && /[A-Za-z]/.test(m2[1])) {
      return { name: m2[1].trim(), phone: '' };
    }
    // Pattern 3: phone-date
    const m3 = base.match(/^(\+?\d{6,15})/);
    if (m3) {
      return { name: '', phone: m3[1].replace(/^\+?(91)?/, '').slice(-10) };
    }
    return { name: '', phone: '' };
  };

  const fileMeta = parseFilenameMeta(call.filename);
  const customerName = call.detected_seller_name || fileMeta.name || call.seller_name || 'Unknown caller';
  const customerPhone = fileMeta.phone || '';
  const initials = (customerName || '?')
    .split(/\s+/)
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const callDateRaw = call.call_date || call.uploaded_at;
  const dateLong = callDateRaw ? new Date(callDateRaw).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
  const timeStr = callDateRaw ? new Date(callDateRaw).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const durationStr = `${Math.floor((call.duration || 0) / 60)}:${Math.floor((call.duration || 0) % 60).toString().padStart(2, '0')}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="call-detail-page">
      
      {/* Back Button - uses browser history */}
      <button 
        onClick={handleGoBack}
        className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600 text-sm mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to calls
      </button>

      {/* Hero Section — avatar + customer info + verdict + meta */}
      <div className="mb-10">
        <div className="flex items-start gap-5 mb-6">
          {/* Avatar circle */}
          <div
            className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-sm ${
              isInterested
                ? 'bg-gradient-to-br from-[#2eb4ac] to-emerald-500'
                : 'bg-gradient-to-br from-slate-500 to-slate-700'
            }`}
            data-testid="hero-avatar"
          >
            {initials || <User className="w-7 h-7" />}
          </div>

          <div className="flex-1 min-w-0">
            <h1
              className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight tracking-tight mb-2 break-words"
              data-testid="hero-name"
            >
              {customerName}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              {customerPhone && (
                <span className="inline-flex items-center gap-1.5">
                  <PhoneIcon className="w-3.5 h-3.5" />
                  {customerPhone}
                </span>
              )}
              {(call.seller_name && call.seller_name !== customerName) && (
                <>
                  {customerPhone && <span className="text-slate-300">·</span>}
                  <span>{call.seller_name}</span>
                </>
              )}
              {call.seller_id && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono text-xs text-slate-400">{call.seller_id}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Verdict + Meta strip */}
        <div className="flex flex-wrap items-center gap-3">
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ring-1 ${
              isInterested
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                : call.final_verdict === 'Not Interested'
                  ? 'bg-rose-50 text-rose-700 ring-rose-100'
                  : 'bg-amber-50 text-amber-700 ring-amber-100'
            }`}
            data-testid="hero-verdict"
          >
            <span className={`w-2 h-2 rounded-full ${
              isInterested ? 'bg-emerald-500'
              : call.final_verdict === 'Not Interested' ? 'bg-rose-500'
              : 'bg-amber-500'
            }`} />
            {call.final_verdict || 'Not Analysed'}
          </div>
          {call.lifecycle_stage && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ring-1 ${
              call.lifecycle_stage === 'Hot' ? 'bg-orange-50 text-orange-700 ring-orange-100' :
              call.lifecycle_stage === 'Warm' ? 'bg-yellow-50 text-yellow-700 ring-yellow-100' :
              'bg-blue-50 text-blue-700 ring-blue-100'
            }`}>
              {call.lifecycle_stage}
            </div>
          )}
          {call.qc_category && (() => {
            // Same palette as CallLogs so the pill identity is consistent app-wide.
            const QC_PALETTE = {
              'Asked to Share Catalogue': 'bg-emerald-50 text-emerald-700 ring-emerald-100',
              'Requested Order List':     'bg-green-50 text-green-700 ring-green-100',
              'Instock':                  'bg-teal-50 text-teal-700 ring-teal-100',
              'Payment Process':          'bg-cyan-50 text-cyan-700 ring-cyan-100',
              'Enquiry Call':             'bg-blue-50 text-blue-700 ring-blue-100',
              'Offline Website Customer': 'bg-indigo-50 text-indigo-700 ring-indigo-100',
              'Call Back Later':          'bg-yellow-50 text-yellow-700 ring-yellow-100',
              'Price Comparison':         'bg-amber-50 text-amber-700 ring-amber-100',
              'Already Billing':          'bg-orange-50 text-orange-700 ring-orange-100',
              'Wrong Submission':         'bg-rose-50 text-rose-700 ring-rose-100',
              'Wrong Number':             'bg-pink-50 text-pink-700 ring-pink-100',
              'Hung Call':                'bg-red-50 text-red-700 ring-red-100',
              'Incomplete Call':          'bg-slate-100 text-slate-700 ring-slate-200',
              'NA':                       'bg-slate-50 text-slate-400 ring-slate-100',
            };
            const palette = QC_PALETTE[call.qc_category] || 'bg-slate-50 text-slate-700 ring-slate-100';
            return (
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ring-1 ${palette}`}
                data-testid="hero-qc-category"
                title={call.qc_remark ? `${call.qc_category} — ${call.qc_remark}` : call.qc_category}
              >
                <Tag className="w-3.5 h-3.5" />
                {call.qc_category}
              </div>
            );
          })()}
          <span className="hidden sm:block w-px h-5 bg-slate-200 mx-1" />
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400" />
            {dateLong}
          </span>
          {timeStr && (
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-slate-400" />
              {timeStr}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-600 tabular-nums">
            <Timer className="w-4 h-4 text-slate-400" />
            {durationStr}
          </span>
          {call.call_date_source && call.call_date_source !== 'uploaded_at' && (
            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-medium tracking-wider uppercase rounded-full" data-testid="call-date-source">
              {call.call_date_source === 'metadata' ? 'From audio metadata' : 'Manual'}
            </span>
          )}
        </div>

        {/* Analyzed-at timestamp — clearly separate from the call timestamp */}
        {call.analyzed_at && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="analyzed-at">
            <Sparkles className="w-3 h-3 text-slate-300" />
            <span className="font-medium tracking-wider uppercase text-slate-400">Analyzed</span>
            <span className="tabular-nums">
              {new Date(call.analyzed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {' · '}
              {new Date(call.analyzed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
        )}
      </div>

      {/* Audio Player */}
      <AudioPlayer audioUrl={call.audio_url ? `/api/audio/${encodeURIComponent(call.audio_url.split('/').pop())}` : null} />

      {/* Summary (full-width) */}
      <div className="mb-12">
        <BriefSummarySection call={call} isInterested={isInterested} analysis={analysis} />
      </div>

      {/* Tabular Breakdown — full-width, stacked below Summary */}
      <div className="mb-12">
        <TabularBreakdownSection call={call} isInterested={isInterested} analysis={analysis} />
      </div>

      {/* Transcript */}
      <TranscriptSection call={call} />

      {/* Related Calls (other calls from the same seller) */}
      <RelatedCallsSection currentCallId={call.id} filename={call.filename} />


      {/* Delete Button - Bottom */}
      <div className="mt-16 pt-8 border-t border-slate-200">
        <button 
          onClick={async () => {
            if (!window.confirm('Delete this call?')) return;
            try {
              await axios.delete(`${API}/calls/${callId}`);
              toast.success('Deleted');
              window.location.href = '/call-logs';
            } catch { toast.error('Failed'); }
          }}
          className="text-sm text-slate-400 hover:text-rose-500 transition-colors"
        >
          Delete this call
        </button>
      </div>
    </div>
  );
};

export default CallDetail;
